// Relative path (not the @base alias): app.config.ts is also typechecked in the node/shared
// context where the @base alias isn't registered — the alias resolves in .vue (app) files only.
import { ECarts } from '../../../base/app/utils/constants'

export default defineAppConfig({
  // General
  defaultCountry: 'de',
  defaultLang: 'de',
  customer: 'wilo',
  titleSeparator: ' | ',
  companyDesc: 'Wilo',
  useLocalStorage: true,
  useShoppingCartCountry: true, // Shopping cart for each country

  // The price list keeps its projects on the SERVER (cartStorage) and always starts in the project
  // management, so a local copy would only be a stale second truth — and a shared one at that: its
  // content is built inside the cart, never contributed from a second tab, so the cross-tab sync
  // every other cart profits from can only cost it. See `tabLocalCarts` in the base app.config.
  tabLocalCarts: [ECarts.ct_price_list_create],

  // Customer specific
  listArType: [ECarts.ct_favourites, ECarts.ct_product_compare, ECarts.ct_catalogue_create, ECarts.ct_price_list_create, ECarts.ct_download_list, ECarts.ct_shopping_cart],
  ecoMarCountries: 'ro',
  // The Wilo CMS marks crawlers with embparam6=true ("isCrawler") — see botEmbParam in the base app.config.
  botEmbParam: 'embparam6',
  altPic: 'https://cms.media.wilo.com/cdnpicflexible/wilo747860/10365677/wilo747860_1.jpg',

  // Modal sizes. A modal shrink-wraps its content by default (see ui.modal below), which is right
  // as long as that content keeps its size. Where it does not — a tree folding open, a list
  // reloading, a selection switching — the modal resizes under the cursor and becomes unusable.
  // Those pick a frame here via :ui. Deliberately NOT the `fullscreen` prop: it would fit
  // technically, but the customer does not want fullscreen modals and the name would lie.
  modalFrame: {
    // Shrink-wrap: the content brings its own size (a picture, a short question, a table that
    // scrolls on its own). Same as the default below — named so a modal can say so out loud.
    auto: {
      content: 'w-fit max-w-[calc(100vw-2rem)]',
    },
    // Inset frame: fixed margin to the viewport on every side, the body scrolls inside it.
    // max-w/w cancel the shrink-wrap default, translate-* the centering it no longer needs.
    inset: {
      content: 'inset-[6vh_8vw] w-auto max-w-none max-h-none translate-x-0 translate-y-0',
      body: 'min-h-0',
    },
    // Compact dialog: fills a bounded width instead of the viewport. For content that would
    // spread across the whole screen without being any easier to read for it.
    compact: {
      content: 'w-full max-w-[min(90vw,48rem)]',
    },
    // Wide dialog: like `compact`, but for content that genuinely needs the width (a multi-column
    // form). Caps at 1400px and keeps a viewport margin below that, so it insets on narrower
    // screens instead of touching the edges. The height stays automatic — no max-h override here,
    // so the library's cap (100dvh minus margin) and its scrolling body still apply.
    wide: {
      content: 'w-[calc(100vw-2rem)] sm:w-[calc(100vw-4rem)] max-w-[1400px]',
    },
  },

  // UI
  ui: {
    // Icons override
    icons: {
      arrowLeft: 'i-wilo-chevron-left-solid-full',
      arrowRight: 'i-wilo-chevron-right-solid-full',
      check: 'i-wilo-check-solid-full-white',
      chevronDown: 'i-wilo-chevron-down-solid-full',
      chevronLeft: 'i-wilo-chevron-left-solid-full',
      chevronRight: 'i-wilo-chevron-right-solid-full',
      chevronUp: 'i-wilo-chevron-up-solid-full',
      close: 'i-wilo-xmark-solid-full',
      external: 'i-wilo-arrow-up-right-solid-full',
      minus: 'i-wilo-minus-solid-full',
    },
    // Card
    card: {
      slots: {
        body: 'p-0 sm:p-0',
        header: 'p-0 sm:p-0',
        footer: 'p-0 sm:p-0',
      },
      variants: {
        variant: {
          // ⚑ The text colour belongs WITH the background. Nuxt UI ships `solid` as
          // `bg-inverted text-inverted` — a dark card with light text. Overriding only the
          // background left the light text on a light card: invisible for anything that is not a
          // heading (`#__nuxt h1..h5` forces text-gunmetal of its own). That is why plain text in
          // these cards used to need `text-gunmetal` on every single element.
          solid: {
            root: 'bg-gunmetal-3 text-gunmetal',
          },
          outline: {
            root: 'shadow-[0_0_8px_#00000029] divide-y-0  ring-0 group',
          },
        },
      },
    },
    // Button
    button: {
      slots: {
        base: [
          'font-bold disabled:opacity-100 aria-disabled:opacity-100 rounded-lg cursor-pointer',
        ],
        trailingIcon: 'shrink-0 ml-auto',
      },
      variants: {
        color: {
          tertiary: '',
          light: '',
        },
        variant: {
          shadow: 'bg-white hover:shadow-[0_0_8px_#00000029] hover:text-green text-xs md:text-xs-md font-normal',
          shadowBold: 'hover:shadow-[0_0_8px_#00000029] hover:bg-white hover:text-green',
        },
        size: {
          md: {
            base: 'px-4 py-2',
            leadingIcon: 'size-5.5',
          },
        },
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: 'solid',
          class: 'hover:bg-green-sub disabled:bg-gunmetal-2 active:bg-gunmetal',
        },
        {
          color: 'secondary',
          variant: 'solid',
          class: 'hover:bg-yellow-sub disabled:bg-gunmetal-2 active:bg-gunmetal',
        },
        {
          color: 'white',
          variant: 'solid',
          class: 'text-gunmetal hover:bg-white hover:text-primary',
        },
        {
          color: 'primary',
          variant: 'outline',
          class: 'hover:text-green-sub hover:ring-green-sub hover:bg-transparent active:bg-transparent disabled:text-gunmetal-2 disabled:ring-gunmetal-2 active:text-gunmetal active:ring-gunmetal',
        },
        {
          color: 'primary',
          variant: 'ghost',
          class: 'p-0 hover:text-green-sub hover:bg-transparent active:bg-transparent disabled:text-gunmetal-2 active:text-green',
        },
        {
          color: 'secondary',
          variant: 'ghost',
          class: 'p-0 hover:text-yellow-sub hover:bg-transparent active:bg-transparent disabled:text-gunmetal-2 active:text-yellow',
        },
        {
          color: 'tertiary',
          variant: 'ghost',
          class: 'p-0 hover:bg-transparent active:bg-transparent disabled:text-gunmetal-2 active:text-gunmetal',
        },
        {
          color: 'tertiary',
          variant: 'outline',
          class: 'hover:text-gunmetal-1 hover:ring-gunmetal-1 hover:bg-transparent active:bg-transparent disabled:text-gunmetal-2 disabled:ring-gunmetal-2 active:text-gunmetal active:ring-gunmetal',
        },
        {
          color: 'primary',
          variant: 'shadow',
          class: 'px-2 py-2 text-primary',
        },
        {
          color: 'tertiary',
          variant: 'shadow',
          class: 'px-2 py-2 text-gunmetal',
        },
        {
          color: 'light',
          variant: 'shadow',
          class: 'px-2 py-2 text-gunmetal-2',
        },
        {
          color: 'tertiary',
          variant: 'shadowBold',
          class: 'bg-gunmetal text-white',
        },
        {
          color: 'white',
          variant: 'shadowBold',
          class: 'bg-white text-gunmetal',
        },
      ],
    },
    // Tab
    tabs: {
      variants: {
        size: {
          sm: {
            trigger: 'px-3 py-1.5 text-base md:text-base-md gap-1.5',
            label: 'px-2',
          },
          md: {
            trigger: 'px-3 py-1.5 font-bold text-lg md:text-lg-md gap-1.5',
            label: 'px-4',
          },
        },
      },
      compoundVariants: [
        {
          orientation: 'horizontal',
          variant: 'link',
          size: 'md',
          class: {
            list: 'block px-0 border-b-6 border-gunmetal-4 -mb-px w-auto',
            indicator: '-bottom-1.5 h-1.5',
          },
        },
        {
          orientation: 'vertical',
          variant: 'link',
          size: 'sm',
          class: {
            content: 'mb-auto',
            list: 'mb-auto border-0',
            indicator: 'w-[4px] rounded-none',
            trigger: 'data-[state=active]:font-bold py-2',
          },
        },
      ],
      defaultVariants: {
        variant: 'link',
      },
    },
    // Breadcrumb
    breadcrumb: {
      variants: {
        active: {
          true: {
            link: 'text-gunmetal-2 hover:text-gunmetal-2 font-normal',
          },
          false: {
            link: 'text-gunmetal-2 hover:text-gunmetal-2',
          },
        },
      },
      compoundVariants: [
        {
          disabled: false,
          active: false,
          to: true,
          class: {
            link: [
              'text-gunmetal-2 hover:text-gunmetal-2',
            ],
          },
        },
      ],
    },
    // Checkbox
    checkbox: {
      slots: {
        base: 'rounded-none ring-color-gunmetal bg-white',
        label: 'leading-[1.9]',
      },
      variants: {
        size: {
          md: {
            container: 'h-7',
          },
        },
      },
      defaultVariants: {
        size: 'md',
      },
    },
    radioGroup: {
      slots: {
        base: 'ring-color-gunmetal bg-white',
        label: 'leading-[1.9]',
      },
      variants: {
        size: {
          md: {
            container: 'h-7',
          },
        },
      },
      defaultVariants: {
        size: 'md',
      },
    },
    // Modal
    modal: {
      slots: {
        overlay: 'z-5000',
        content: 'z-5001',
        header: 'mr-8',
        title: 'text-lg',
      },
      variants: {
        fullscreen: {
          // Shrink-wrap the content instead of Nuxt UI's fixed max-w-lg, up to nearly the full
          // viewport width. This is what the previous `max-w-min max-w-max` pair amounted to (of a
          // conflicting pair tailwind-merge keeps the last, so only max-w-max ever applied) — kept
          // deliberately, a tighter cap would squeeze the wide modals (catalog, settings, image).
          // What that pair also did was switch off the height cap via max-h-max, so a modal grew
          // out of the viewport instead of scrolling. Dropping it hands the height cap and the
          // scrolling body back to the library's compound variant (scrollable:false).
          false: {
            content: 'w-fit max-w-[calc(100vw-2rem)]',
          },
        },
      },
    },
    // Popover
    // Same band and same reason as the select popper (5010): the theme ships the popover without a
    // z-index, so inside a modal (content 5001) it painted BEHIND it. Found 24.09.2026 on the price
    // list's catalog search — the suggestions were rendered, filled, and invisible under the modal.
    // Below the tooltip (5020), which stays the topmost layer.
    popover: {
      slots: {
        content: 'z-5010',
      },
    },
    // Tooltip
    // The Nuxt UI theme ships the tooltip WITHOUT any z-index, and TooltipPortal teleports the
    // content into #__nuxt (see UApp portal in AppIndex.vue) — so it competes there with the
    // modal and the select popper as a real sibling. A positioned element with z-index auto
    // paints in step 8 of the painting order and one with a positive z-index in step 9,
    // REGARDLESS of DOM order, so the tooltip lost against anything positioned however late it
    // was mounted. That is what left tooltips half covered.
    // 5020 puts it above the whole band on purpose: overlay 5000, modal content 5001, select
    // popper 5010. A tooltip belongs to the element the cursor is on, and that element can sit
    // inside a modal (SettingsSmall, Compare, CartIcons) or on a select item — anything lower
    // would hide it exactly there. Keep it the topmost layer; new overlays go BELOW it.
    tooltip: {
      slots: {
        content: 'z-5020',
      },
    },
    // Accordion
    accordion: {
      slots: {
        root: 'w-full',
        item: 'border-b-2 border-gunmetal-4 last:border-b-0 data-[state=open]:border-gunmetal-4 pt-8  cursor-pointer',
        header: 'flex ',
        trigger: 'group flex-1 flex items-center gap-1.5 font-medium text-lg md:text-lg-md py-4 focus-visible:outline-primary min-w-0  cursor-pointer',
        content: 'data-[state=open]:animate-[accordion-down_200ms_ease-out] data-[state=closed]:animate-[accordion-up_200ms_ease-out] overflow-hidden focus:outline-none pt-4',
        body: 'pb-4',
        leadingIcon: 'shrink-0 size-5',
        trailingIcon: 'shrink-0 size-5 ms-auto rotate-135 group-data-[state=open]:rotate-180 transition-transform duration-200W group-data-[state=open]:text-primary mr-0',
        label: 'text-start break-words font-bold pl-0 group-data-[state=open]:text-primary',
      },
    },
    // Container
    container: {
      base: 'print:max-w-full print:px-0',
    },
    // Carousel
    carousel: {
      slots: {
        root: 'sm:px-14',
        prev: 'rounded-lg bg-gunmetal-4 hover:bg-gunmetal-3 ring-0',
        next: 'rounded-lg bg-gunmetal-4 hover:bg-gunmetal-3 ring-0',
      },
      variants: {
        orientation: {
          horizontal: {
            prev: 'sm:start-0',
            next: 'sm:end-0',
          },
        },
      },
    },
    // Select
    select: {
      slots: {
        trailingIcon: 'text-gunmetal',
        content: 'z-5010',
      },
      variants: {
        variant: {
          outline: 'ring-gunmetal',
        },
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: [
            'outline',
          ],
          class: 'focus:ring-1 focus:ring-inset focus:ring-gunmetal hover:ring-gunmetal-1',
        },
      ],
    },
    // Input
    input: {
      variants: {
        variant: {
          outline: 'ring-gunmetal',
        },
        size: {
          xl: {
            base: 'px-6 py-4 text-base gap-2',
            leading: 'ps-6',
            trailing: 'pe-6',
          },
        },
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: [
            'outline',
          ],
          class: 'focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-gunmetal hover:ring-gunmetal-1',
        },
      ],
    },
    // InputDate
    inputDate: {
      slots: {
        segment: 'data-invalid:text-gunmetal',
      },
      variants: {
        variant: {
          outline: 'ring-gunmetal',
        },
        size: {
          md: {
            base: [
              'pr-18 ',
            ],
            segment: 'data-[segment=day]:w-5 data-[segment=month]:w-5 data-[segment=year]:w-10',
          },
        },
      },
    },
    // Chip
    chip: {
      slots: {
        base: 'py-2 px-1.5 text-lg ring-0',
      },
      variants: {
        size: {
          md: 'h-[12px] min-w-[12px] text-[12px]',
        },
      },
    },
    // Slider
    /* slider: {
      slots: {
      },
    }, */
  },
})
